import { useQuery } from '@apollo/client';
import { Spinner, Stack, Text } from '@fluentui/react';
import EmptyApprovals from 'assets/svgs/chart-placeholders/approvals.svg?react';
import EmptyDocuments from 'assets/svgs/chart-placeholders/documents.svg?react';
import { useCommonStyles } from 'common/styles';
import { ChartLayoutType } from 'common/types/globalTypes';
import React, { useEffect } from 'react';
import { isDashBoardScreen } from 'utility/cache/ui';
import { UserDashboardCharts } from './__generated__/UserDashboardCharts';
import {
  ApprovalsChart,
  DepartmentsChart,
  DocumentsChart,
  LocationsChart,
  MyTransactionStatusChart,
  PayableChart,
  ProductionsChart,
  SuppliersChart,
  TagsChart,
  TransactionsChart,
  TransactionStatusChart,
  TravelAuthorizationChart,
} from './Charts';
import {
  ChartContainer,
  ChartContainerProps,
  ChartSize,
} from './Charts/common';
import { MyPOStatusChart, POStatusChart } from './Charts/POStatusChart';
import { POTagsChart } from './Charts/POTagsChart';
import { MyTAStatusChart, TAStatusChart } from './Charts/TAStatusChart';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import DASHBOARD_CHARTS_QUERY from './UserDashboardCharts.graphql';

const chartLayoutToContainerProps = (
  layout: ChartLayoutType
):
  | Pick<ChartContainerProps<any>, 'chart' | 'size' | 'showOnEmpty'>
  | undefined => {
  switch (layout) {
    case 'KPI_AGED_PAYABLES':
      return {
        chart: PayableChart,
        size: ChartSize.Medium,
      };
    case 'KPI_APPROVAL_TYPES':
      return {
        chart: ApprovalsChart,
        size: ChartSize.Medium,
        showOnEmpty: {
          Icon: EmptyApprovals,
          text: 'No pending approvals.',
        },
      };
    case 'KPI_BUSINESS_UNITS':
      return {
        chart: LocationsChart,
      };
    case 'KPI_COMPANIES':
      return {
        chart: ProductionsChart,
      };
    case 'KPI_DEPARTMENTS':
      return {
        chart: DepartmentsChart,
      };
    case 'KPI_DOCUMENT_BREAK_DOWN':
      return {
        chart: DocumentsChart,
        size: ChartSize.Medium,
        showOnEmpty: {
          Icon: EmptyDocuments,
          text: "There aren't any recent files.",
        },
      };
    case 'KPI_SUPPLIERS':
      return {
        chart: SuppliersChart,
      };
    case 'KPI_TAGS':
      return {
        chart: TagsChart,
        size: ChartSize.Medium,
      };
    case 'KPI_PO_TAGS':
      return {
        chart: POTagsChart,
        size: ChartSize.Medium,
      };
    case 'KPI_TRANSACTION_TYPES':
      return {
        chart: TransactionsChart,
      };
    case 'KPI_TRANSACTION_STATUS':
      return {
        chart: TransactionStatusChart,
      };
    case 'KPI_MY_TRANSACTION_STATUS':
      return {
        chart: MyTransactionStatusChart,
      };
    case 'KPI_TRAVEL_AUTHORIZATION_POLICY_BREAKDOWN':
      return {
        chart: TravelAuthorizationChart,
      };
    case 'KPI_PO_STATUS':
      return {
        chart: POStatusChart,
      };
    case 'KPI_MY_PO_STATUS':
      return {
        chart: MyPOStatusChart,
      };
    case 'KPI_TA_STATUS':
      return {
        chart: TAStatusChart,
      };
    case 'KPI_MY_TA_STATUS':
      return {
        chart: MyTAStatusChart,
      };
    default:
      return undefined;
  }
};

export const Dashboard: React.FC = () => {
  const commonStyles = useCommonStyles();
  const { data, loading } = useQuery<UserDashboardCharts>(
    DASHBOARD_CHARTS_QUERY,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    }
  );
  useEffect(() => {
    isDashBoardScreen(true);
    return () => {
      isDashBoardScreen(false);
    };
  }, []);

  return (
    <Stack style={{ height: '100%', overflowX: 'hidden' }}>
      <Stack className={commonStyles.listHeaderContainer}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
          <Text variant="xLargePlus">Spend Analysis</Text>
          <HelpIcon appLinkType="MENU_MAIN" />
        </Stack>
      </Stack>

      {loading ? (
        <Stack
          grow
          verticalFill
          horizontal
          horizontalAlign="center"
          verticalAlign="center"
        >
          <Spinner label="Loading..." />
        </Stack>
      ) : (
        <Stack
          horizontal
          wrap
          tokens={{ childrenGap: 30, padding: '15px 30px' }}
          grow
        >
          {[...(data?.userDashboardWebChartsList || [])]
            ?.sort((c1, c2) => c1.chartPosition - c2.chartPosition)
            .map((c) => {
              const props = chartLayoutToContainerProps(c.chartLayout);
              return props ? (
                <ChartContainer
                  key={c.chartLayout}
                  title={c.caption}
                  isManualRefresh={!!c.isManualRefresh}
                  timedRefreshMilliseconds={c.timedRefreshMilliseconds}
                  {...props}
                />
              ) : null;
            })}
        </Stack>
      )}
    </Stack>
  );
};
